/**
 * GlobeTakeover — Full-bleed globe surface (S3.2).
 *
 * Replaces the workspace canvas with the globe + layer panel (left) +
 * OSINT feed (right) per design ref
 * `design_handoff_echelon_2026_04_26/workspace/app.jsx:293-392`.
 *
 * Owns:
 *   - Live signal corpus via `useWorldMonitorLive`
 *   - Layer toggle state via `useGlobeLayerState`
 *   - Filtering pipeline: live signals → isDomainVisible → OsintFeed
 *
 * Does NOT own:
 *   - Selected signal / investigation (parent controls drawers via `children`)
 *   - Open/close lifecycle (parent gates whether the takeover is mounted)
 *   - Feed visibility (parent passes `osintFeedOpen`)
 */

import { useEffect, useMemo, useRef } from 'react';
import { cn } from '@/lib/utils';
import { useWorldMonitorLive } from '../../hooks/useWorldMonitorLive';
import {
  GLOBE_LAYER_DEFS,
  isDomainVisible,
  useGlobeLayerState,
  type GlobeLayerKey,
  type GlobeLayerState,
} from '../../hooks/useGlobeLayerState';
import type { FocusInvestigation } from './GlobeCanvas';
import { GlobeCanvas } from './GlobeCanvas';
import { LayerPanel } from './LayerPanel';
import { OsintFeed } from './OsintFeed';
import { LineageTimelineStrip } from '../environment/LineageTimelineStrip';
import type { WorldMonitorSignal } from '../../api/worldMonitor';

export interface GlobeTakeoverProps {
  /** Beacon-click + feed-row-click handler. Parent owns selection state and
   * mounts drawers via `children`; selected-highlight pass-through to
   * GlobeCanvas is wired in S3.3 when SignalDrawer lands. */
  onSelectSignal: (signal: WorldMonitorSignal) => void;
  /** Fly-to target. */
  focusInv?: FocusInvestigation | null;
  /** Globe style variant (stipple | wireframe). */
  globeStyle?: 'stipple' | 'wireframe';
  /** Whether the right-rail OSINT feed is visible. */
  osintFeedOpen: boolean;
  /** Close handler for the OSINT feed (parent flips `osintFeedOpen`). */
  onOsintFeedClose: () => void;
  /** Drawers + other overlays rendered above the takeover surface. */
  children?: React.ReactNode;
  /** Best-effort layer-scope hint from the `/workspace?globe=1&domain=<d>`
   * deep-link (sprint-241 FR-S6.3). Applied ONCE per value: a matching
   * domain enables that layer group and disables the other signal groups
   * (`deriveDomainScopedLayerState`); an unknown domain leaves the layer
   * state UNCHANGED (honest no-op — no invented layer groups) and the
   * takeover stays open either way. */
  domainScope?: string | null;
  /** Optional className passthrough (positioning by parent). */
  className?: string;
}

export function GlobeTakeover({
  onSelectSignal,
  focusInv,
  globeStyle = 'stipple',
  osintFeedOpen,
  onOsintFeedClose,
  children,
  domainScope,
  className,
}: GlobeTakeoverProps) {
  const { data: liveData } = useWorldMonitorLive();
  const { state: layerState, toggle: onToggleLayer, applyDomainScope } = useGlobeLayerState();

  // Apply the deep-link domain scope ONCE per value (the parent consumes the
  // URL params once and pins the hint, so this fires once per arrival; the
  // ref guards strict-mode double-effects). Manual toggles afterwards win.
  const appliedScopeRef = useRef<string | null>(null);
  useEffect(() => {
    if (!domainScope || appliedScopeRef.current === domainScope) return;
    appliedScopeRef.current = domainScope;
    applyDomainScope(domainScope);
  }, [domainScope, applyDomainScope]);

  const filteredSignals = useMemo(
    () => filterSignalsByLayerState(liveData.signals, layerState),
    [liveData.signals, layerState],
  );

  const counts = useMemo(() => signalCountsByLayer(liveData.signals), [liveData.signals]);

  return (
    <div
      data-testid="globe-takeover"
      className={cn(
        'absolute inset-0 z-10 fade-in flex flex-col',
        // Fixes undefined --e-bg-deep (bead 2oe7) — theme-correct deep background behind the globe region.
        'bg-[var(--bg-deep)]',
        className,
      )}
    >
      {/* Globe region — panels + drawers (children) are bounded here so they
          end above the bottom lineage strip, per the Globe View frame. */}
      <div className="relative flex-1 min-h-0">
        <div className="absolute inset-0">
          <GlobeCanvas
            liveData={liveData}
            globeLayers={layerState}
            focusInv={focusInv}
            style={globeStyle}
            onSelectSignal={onSelectSignal}
          />
        </div>

        <div className="absolute left-4 top-4 bottom-4 z-20 pointer-events-auto">
          <LayerPanel state={layerState} onToggle={onToggleLayer} counts={counts} />
        </div>

        {osintFeedOpen && (
          <div className="absolute right-4 top-4 bottom-4 z-20 pointer-events-auto">
            <OsintFeed
              signals={filteredSignals}
              onSelect={onSelectSignal}
              onClose={onOsintFeedClose}
            />
          </div>
        )}

        {children}
      </div>

      {/* Bottom lineage timeline stays visible during the takeover (carry-item
          239.carry / bead 25ba, Workspace Globe View frame bottom edge). Same
          component + cross-theatre props as the below-canvas Workspace mount
          (InvestigationWorkspacePageV2.tsx); React-Query dedupes the runs
          fetch — both mounts share queryKey ['environmentRuns', {limit: 50},
          ...] so no second polling loop is created. */}
      <LineageTimelineStrip filters={{ limit: 50 }} />
    </div>
  );
}

// ── Pure helpers (exported for vitest) ─────────────────────────────

export function filterSignalsByLayerState(
  signals: WorldMonitorSignal[],
  state: GlobeLayerState,
): WorldMonitorSignal[] {
  return signals.filter((s) => isDomainVisible(s.domain, state));
}

/**
 * Aggregate signal counts keyed by GlobeLayerKey (the LayerPanel toggle id),
 * not by SignalDomain. `markets` and `environment-health` bundle multiple
 * domains; the panel needs the bundled count, not per-domain counts.
 */
export function signalCountsByLayer(
  signals: WorldMonitorSignal[],
): Partial<Record<GlobeLayerKey, number>> {
  const domainToKey: Record<string, GlobeLayerKey> = {};
  for (const def of GLOBE_LAYER_DEFS) {
    for (const domain of def.domains) {
      domainToKey[domain] = def.id;
    }
  }
  const counts: Partial<Record<GlobeLayerKey, number>> = {};
  for (const s of signals) {
    const key = domainToKey[s.domain];
    if (!key) continue;
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}
